import { Injectable } from '@angular/core';

import { asyncScheduler, BehaviorSubject, forkJoin, Observable, of, scheduled, map } from 'rxjs';

import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { FolderApi } from '../folders/models/folder.api.model';
import { MentionModel, PartialMentionApiModel } from './model/mention.model';
import { MentionsSearchResponseModel } from './model/mentions-search-response.model';
import { SearchCriteriaModel } from './model/search-criteria.model';
import { SearchCriteriaQueryModel } from './model/search-criteria-query.model';
import { MentionIdMercuryMapModel } from './model/mention-id-mercury-map.model';
import { MentionsBatchResponseApiModel } from './model/mentions-batch-response-api.model';
import { CreateMentionApiModel } from './model/create-mention-api.model';
import { BulkUpdateMentionRequestModel } from './model/bulk-update-mention-request.model';
import { BulkMentionSourceRequestModel } from './model/bulk-mentions-source-request.model';
import { SaveMentionsRequestModel } from './model/save-mentions-request.model';
import { SaveMentionsApiModel } from './model/save-mentions-api.model';
import { MediaViewRequestApiModel } from './model/instaplay-request.api.model';
import { NeptuneApi } from '../genericApi/neptune.api';
import { TVEyesArchivedStateEnum } from './enum/tv-eyes-archived-state.enum';
import { MentionImportRequestModel, MentionImportResponseModel } from '../../Monitor/Shared/services/mention-csv-import-dialog.service';
import { TVEyesTimelineThumbnailRequestApiModel } from './model/tveyes-timeline-thumbnail-request-api.model';
import { TVEyesTimelineThumbnailApiModel } from './model/tveyes-timeline-thumbnail-api.model';

export type MentionPatchModel = PartialMentionApiModel & {
    tveyesStartDateTime?: string;
};

export type TVEyesArchivePatchModel = TVEyesDirectArchivePatchModel | {
    id: string;
    isDirectArchive: false;
    clipDuration: number;
    body: string;
    tveyesArchivedState: TVEyesArchivedStateEnum;
    tvEyesInstaPlayUrl: string;
    startDateTime: number;
};

interface TVEyesDirectArchivePatchModel {
    id: string;
    isDirectArchive: true;
}


@Injectable()
export class MentionsApi {

    public static MAX_BATCH_MENTION_SIZE: number = 200;

    constructor(
        private neptuneApi: NeptuneApi
    ) {}

    /**
     * Gets all mention folders for the current account
     *
     * @returns void
    */
    getAllFolders(): Observable<FolderApi[]> {
        return this.neptuneApi.api<FolderApi[]>(
            '/mentions/folders',
            {},
            HttpVerbsEnum.GET
        );
    }

    /**
     * Create a set of saved mentions
     *
     * @param mentions A list of mentions objects
     *
     * @returns An observable that returns the a mapping of mention ids to mercury ids
     */
    createMentionsFromMercury(saveMentionsRequestModel: SaveMentionsRequestModel): Observable<MentionIdMercuryMapModel[]> {
        if (saveMentionsRequestModel.mercuryIds.length === 0) {
            // No mentions to add, so return an empty list and don't perform a request
            return of([]);
        }

        return this.neptuneApi.api<MentionIdMercuryMapModel[]>(
            '/mentions/batch',
            saveMentionsRequestModel,
            HttpVerbsEnum.POST,
            {}
        );
    }

    /**
     * Gets a saved mention
     *
     * @param mentionId The id of a saved mention
     *
     * @returns An observable that returns the saved mention
     */
    getMention(mentionId: string): Observable<MentionModel> {
        return this.neptuneApi.api<null>(
            `/mentions/${mentionId}`,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Get a set of mentions by id
     *
     * THIS CALL IS NOT BATCHED, IF YOU REQUEST TOO MANY MENTION IDS THE CALL WILL FAIL (414)
     *
     * @param mentionIds A list of mentions ids
     *
     * @returns An observable that returns the a set of mention objects
     */
    getMentions(mentionIds: string[]): Observable<MentionsBatchResponseApiModel> {
        if (mentionIds.length < 1) {
            // No mentions to add, so return an empty list and don't perform a request
            return of({});
        }

        return this.neptuneApi.api<MentionsBatchResponseApiModel>(
            '/mentions/batch/fetch',
            mentionIds,
            HttpVerbsEnum.POST,
            {}
        );
    }

    /**
     * Fetches mentions in batches, respecting a URL limit of 2048
     */
    getMentionsBatched(mentionIds: string[]): Observable<MentionsBatchResponseApiModel> {
        if (mentionIds.length < 1) {
            // No mentions to add, so return an empty list and don't perform a request
            return of({});
        }

        const requestSlices: string[][] = [];
        let startIndex = 0;
        while (startIndex < mentionIds.length) {
            // Slice the mentionIds into arrays no more than MAX_BATCH_MENTION_SIZE in length
            requestSlices.push(mentionIds.slice(startIndex, startIndex + MentionsApi.MAX_BATCH_MENTION_SIZE));
            startIndex += MentionsApi.MAX_BATCH_MENTION_SIZE;
        }

        const requests: Observable<MentionsBatchResponseApiModel>[] = [];
        for (const requestSlice of requestSlices) {
            requests.push(
                this.getMentions(requestSlice)
            );
        }

        return forkJoin(requests).pipe(
            map<MentionsBatchResponseApiModel[], MentionsBatchResponseApiModel>(
                (results) => {
                    // Results is an array of MentionsBatchResponseApiModel, need to flatten them into a single MentionsBatchResponseApiModel
                    const merged: MentionsBatchResponseApiModel = {};

                    results.forEach(
                        (result) => {
                            for (const mentionId of Object.keys(result)) {
                                merged[mentionId] = result[mentionId];
                            }
                        }
                    );
                    return merged;
                }
            )
        );
    }


    /**
     * Get a set of mentions by mercury ids
     *
     * THIS CALL IS NOT BATCHED, IF YOU REQUEST TOO MANY MENTION IDS THE CALL WILL FAIL (414)
     *
     * @param mentionIds A list of mentions ids
     *
     * @returns An observable that returns the a set of mention objects
     */
    getMentionsByMercuryIds(ids: string[], isAddTvEyesInstaPlayUrls?: boolean): Observable<MentionModel[]> {
        if (ids.length < 1) {
            // No mentions to add, so return an empty list and don't perform a request
            return of([]);
        }

        const query: Record<string, boolean> = {};
        if (isAddTvEyesInstaPlayUrls) {
            query.isAddTvEyesInstaPlayUrls = true;
        }

        return this.neptuneApi.api<MentionModel[]>(
            '/mentions/batch/mercurymentions',
            ids,
            HttpVerbsEnum.POST,
            query
        );
    }

    /**
     * Fetches mercury mentions in batches, respecting a URL limit of 2048
     */
    getMercuryMentionsBatched(mentionIds: string[], isAddTvEyesInstaPlayUrls?: boolean): Observable<MentionModel[]> {
        if (mentionIds.length < 1) {
            // No mentions to add, so return an empty list and don't perform a request
            return of([]);
        }

        const requestSlices: string[][] = [];
        let startIndex = 0;
        while (startIndex < mentionIds.length) {
            // Slice the mentionIds into arrays no more than MAX_BATCH_MENTION_SIZE in length
            requestSlices.push(mentionIds.slice(startIndex, startIndex + MentionsApi.MAX_BATCH_MENTION_SIZE));
            startIndex += MentionsApi.MAX_BATCH_MENTION_SIZE;
        }

        const requests: Observable<MentionModel[]>[] = [];
        for (const requestSlice of requestSlices) {
            requests.push(
                this.getMentionsByMercuryIds(requestSlice, isAddTvEyesInstaPlayUrls)
            );
        }

        return forkJoin(requests).pipe(
            map(
                (results) => {
                    const res = [];

                    results.forEach(
                        (result) => {
                            res.push(...result);
                        }
                    );
                    return res;
                }
            )
        );
    }

    /**
     * Track mention view
     */
    trackMentionView(mention: MentionModel): Observable<null> {
        return this.neptuneApi.api<null>(
            `/mentions/${mention.mercuryId}/track`,
            null,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Updates an existing saved mention
     */
    updateMention(mention: MentionModel, applyAutoTagging: boolean = false): Observable<null> {
        const mentionId = mention.id;
        delete mention.images;
        delete mention.harvestedDate;
        delete mention.isLexisNexis;
        delete mention.id;

        return this.neptuneApi.api<null>(
            `/mentions/${mentionId}?applyTaggingSearches=${applyAutoTagging}`,
            mention,
            HttpVerbsEnum.PUT
        );
    }

    /**
     * Deletes a list of mentions by calling deleteMention for each item in the list
     * @param mentions a list of mentions
     *
     * @returns Observable returns null[] if sucessful
     */
    deleteMentions(mentions: MentionModel[], query?: SearchCriteriaQueryModel, reason?: string): Observable<any> {
        // get all ids for saved mentions (removes null ids)
        const mentionIds = mentions.map(mention => mention.id).filter(id => id);
        const bulkRequest: BulkMentionSourceRequestModel = {
            mentionIds: mentionIds,
            query: query,
            reason: reason
        };
        return this.deleteMentionsInBulk(bulkRequest);
    }

    /**
     * Deletes a saved mention
     * @param mention A mention that has already been saved at least once
     *
     * @returns An observable that returns null after the delete was successful
     */
    deleteMention(mention: MentionModel | string): Observable<null> {
        let id: string;
        if (mention instanceof MentionModel) {
            id = mention.id;
        } else {
            id = mention;
        }

        return this.neptuneApi.api<null>(
            `/mentions/${id}`,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    /**
     * Saves a list of mentions to the current user's account
     *
     * @param mentions An array of mention objects
     *
     * @returns An observable that returns when the save was successful
     */
    saveMentions(saveMentions: SaveMentionsApiModel): Observable<MentionIdMercuryMapModel[]> {
        const saveExistingMentionsToFolder = saveMentions.existingMentionsToFolderRequest;
        const saveNewMentions = saveMentions.newMentionsRequest;
        const numberOfNewMentions = saveMentions.newMentionsRequest.mercuryIds.length;

        if (
            numberOfNewMentions === 0
            && (saveNewMentions.folderId === null || saveNewMentions.folderId === undefined) && !(saveNewMentions.folderIds?.length > 0)
        ) {
            // avoid unnecesary network call
            return scheduled([saveMentions.existingMentionMappings], asyncScheduler);
        }

        let saveNewMentionsRequest = of([]);
        if (saveNewMentions.mercuryIds.length) {
            saveNewMentionsRequest = this.createMentionsFromMercury(saveNewMentions);
        }

        let saveExistingMentionsRequest = of([]);
        if ((saveExistingMentionsToFolder?.modifications?.addFolder || saveExistingMentionsToFolder?.modifications?.addFolders)
                && saveExistingMentionsToFolder.mentionIds.length) {
            saveExistingMentionsRequest = this.updateMentionsInBulk(saveExistingMentionsToFolder);
        }

        return forkJoin([
            saveNewMentionsRequest,
            saveExistingMentionsRequest
        ]).pipe(
            map(joinedResult => {
                return [...joinedResult[0], ...saveMentions.existingMentionMappings];
            })
        );
    }

    /**
     * Removes a list of mentions from a folder
     *
     * @param mention An array of mention objects
     * @param folder A mention folder object
     *
     * @returns An observable that returns when the save was successful
     */
    removeMentionFromFolder(mention: MentionModel, folder: FolderApi): Observable<any> {
        if (!mention.folderIds) {
            mention.folderIds = [];
        }

        const index = mention.folderIds.indexOf(folder.id);

        if (index >= 0) {
            mention.folderIds.splice(index, 1);
        } else {
            // already not in the folder, so doesn't need to be updated
            return new BehaviorSubject<null>(null);
        }

        return this.updateMention(mention);
    }

    /**
     * Searches an account for saved mentions
     *
     * @param body A search criteria model
     *
     * @returns An observable that returns a Mentions search response
     */
    searchAccount(body: SearchCriteriaModel): Observable<MentionsSearchResponseModel> {
        return this.neptuneApi.api<MentionsSearchResponseModel>(
            '/saved-mentions/search',
            body,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Searches an account for saved mentions, but the account does not need AM permissions (used for Monitor / Visibility Intelligence)
     * @param body A search criteria model
     * @returns An observable that returns a Mentions search response
     */
    viSearchAccount(body: SearchCriteriaModel): Observable<MentionsSearchResponseModel> {
        return this.neptuneApi.api<MentionsSearchResponseModel>(
            '/visibility-intelligence/search',
            body,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Searches mercury for saved mentions
     *
     * @param body A search criteria model
     *
     * @returns An observable that returns a Mentions respose
     */
    searchMentions(body: SearchCriteriaModel): Observable<MentionsSearchResponseModel> {
        return this.neptuneApi.api<MentionsSearchResponseModel>(
            '/mentions/search',
            body,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Searches for recent coverages
     *
     * @param body A search criteria model
     *
     * @returns An observable that returns a Mentions respose
     */
    searchRecentCoverages(body: SearchCriteriaModel): Observable<MentionsSearchResponseModel> {
        return this.neptuneApi.api<MentionsSearchResponseModel>(
            '/mentions/recentcoverage',
            body,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Sends a new user generated mention to be saved to the account
     */
    createCustomMention(body: CreateMentionApiModel, applyAutoTagging: boolean = false): Observable<string> {
        return this.neptuneApi.api<string>(
            `/mentions?applyTaggingSearches=${applyAutoTagging}`,
            body,
            HttpVerbsEnum.POST,
            null,
            'text'
        );
    }


    patchTVEyesMention(patchModel: TVEyesArchivePatchModel): Observable<null> {
        const slug: string = `/mentions/${patchModel.id}/tveyesarchive`;

        return this.neptuneApi.api<null>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    patchTVEyesMentions(patchModels: TVEyesArchivePatchModel[]): Observable<string[]> {
        const patchModelMap: {
            [k in string]: MentionPatchModel; // Defines all keys must exist with a string value
        } = {};

        patchModels.forEach(patchModel => {
            patchModelMap[patchModel.id] = patchModel;
        });

        return this.neptuneApi.api<string[]>(
            '/mentions/batch/tveyesarchive',
            patchModelMap,
            HttpVerbsEnum.PATCH,
            {}
        );
    }

    revertTVEyesMention(id: string): Observable<null> {
        // @TODO: switch to /mentions/${id}/tveyes/revert — more semantically correct,
        // since the current endpoint doesn't support archived TVEyes content.
        const slug: string = `/mentions/${id}/tveyesarchive/revert`;

        return this.neptuneApi.api<null>(
            slug,
            null,
            HttpVerbsEnum.PATCH
        );
    }


    /**
     * Patches changes onto a mention, leaving unset fields the way they are
     */
    patchMention(mention: MentionPatchModel, applyAutoTagging: boolean = false): Observable<null> {
        return this.neptuneApi.api<null>(
            `/mentions/${mention.id}?applyTaggingSearches=${applyAutoTagging}`,
            mention,
            HttpVerbsEnum.PATCH
        );
    }


    /**
     * batch patch of mentions, so we don't have to do X amount of api calls of the patchMention()
     */
    patchMentions(mentions: MentionPatchModel[], applyAutoTagging: boolean = false): Observable<null> {
        const mentionsMap: {
            [k in string]: MentionPatchModel; // Defines all keys must exist with a string value
        } = {};

        mentions.forEach(mention => {
            mentionsMap[mention.id] = mention;
        });

        return this.neptuneApi.api<null>(
            `/mentions/batch?applyTaggingSearches=${applyAutoTagging}`,
            mentionsMap,
            HttpVerbsEnum.PATCH,
            {}
        );
    }

    /**
     * Updates mentions in bulk, as admin - so far just used for renormalization
     */
    updateMentionsInBulkAdmin(body: BulkUpdateMentionRequestModel): Observable<null> {
        return this.neptuneApi.api<null>(
            '/saved-mentions/bulk/update/admin',
            body,
            HttpVerbsEnum.POST,
            {},
            'text'
        );
    }

    /**
     * Updates mentions in bulk
     */
    updateMentionsInBulk(body: BulkUpdateMentionRequestModel): Observable<null> {
        return this.neptuneApi.api<null>(
            '/saved-mentions/bulk/update',
            body,
            HttpVerbsEnum.POST,
            {},
            'text'
        );
    }

    /**
     * copies a mention to another account
     */
    copyMentionToAccount(body: BulkUpdateMentionRequestModel): Observable<null> {
        return this.neptuneApi.api<null>(
            '/saved-mentions/bulk/copy-to-account',
            body,
            HttpVerbsEnum.POST,
            {},
            'text'
        );
    }

    /**
     * Deletes mentions in bulk
     */
    deleteMentionsInBulk(body: BulkMentionSourceRequestModel): Observable<MentionModel[]> {
        return this.neptuneApi.api<null>(
            '/saved-mentions/bulk',
            body,
            HttpVerbsEnum.DELETE,
            {},
            'text'
        );
    }


    bulkImport(body: MentionImportRequestModel): Observable<MentionImportResponseModel> {
        const slug: string = '/saved-mentions/bulk/mention-import';

        return this.neptuneApi.api<MentionImportResponseModel>(
            slug,
            body,
            HttpVerbsEnum.POST,
            {},
            'json'
        );
    }

    bulkImportEmailReport(body: any): Observable<void> {
        return this.neptuneApi.api<void>(
            '/saved-mentions/import/notify',
            body,
            HttpVerbsEnum.POST,
            {},
            'text'
        );
    }

    /**
     * @param usePlayStartRegex - Whether to include the PlayStartRegex query parameter,
     * which cues playback to the first regex match. Defaults to true.
     * Set to false when using an alternative start-point parameter such as
     * ModEditorDefaultStartPoint.
     */
    getTVEyesMediaViewUrl(body: MediaViewRequestApiModel, usePlayStartRegex: boolean = true): Observable<string> {
        return this.neptuneApi.api<string>(
            '/mentions/tveyes/mediaview',
            body,
            HttpVerbsEnum.POST,
            {
                isUsePlayStartRegex: usePlayStartRegex
            },
            'text'
        );
    }

    fetchTVEyesTimelineThumbnails(mentionUrl: string, startDateTime: string): Observable<TVEyesTimelineThumbnailApiModel[]> {
        const slug: string = '/mentions/tveyes/thumbnails';
        const body: TVEyesTimelineThumbnailRequestApiModel = {
            url: mentionUrl,
            startDateTime: startDateTime
        };
        return this.neptuneApi.api<TVEyesTimelineThumbnailApiModel[]>(
            slug,
            body,
            HttpVerbsEnum.POST
        );
    }

    validateFilters(body: SearchCriteriaModel): Observable<void> {
        const slug: string = '/mentions/search/validate';

        return this.neptuneApi.api<void>(
            slug,
            body,
            HttpVerbsEnum.POST
        );
    }
}
